// @ts-check

import "./styles/main.css";
import { SudokuBoard } from "./components/sudoku-board.js";
import { CompanionDirector } from "./companion/companion-director.js";
import {
  GameCommandService,
  completionRecord,
} from "./game/command-service.js";
import { indexToCellId } from "./game/capabilities.js";
import { visibleBoard } from "./game/player-view.js";
import { AssetService } from "./services/asset-service.js";
import { UpdateManager } from "./services/update-manager.js";
import { GamepadController } from "./platform/gamepad-controller.js";
import { PlatformAdapter } from "./platform/platform-adapter.js";
import { navigate, parseRoute } from "./router.js";
import { SaveStore } from "./storage/save-store.js";
import { createStore } from "./store/store.js";
import { DEFAULT_SETTINGS, initialState, reducer } from "./store/reducer.js";
import { restoreGame } from "./store/game-state.js";
import { BUILD_ID } from "./globals.js";

class SudokuApp {
  constructor(root) {
    this.root = root;
    this.store = createStore(reducer, initialState);
    this.persistence = new SaveStore();
    this.platform = new PlatformAdapter();
    this.assets = null;
    this.director = null;
    this.board = null;
    this.renderedPlayId = null;
    this.saveTimer = null;
    this.idleTimer = null;
    this.lastTick = performance.now();
    this.controllerState = { connected: false, name: "", digit: 1 };
    this.gamepad = new GamepadController({
      move: (row, column) => this.controllerMove(row, column),
      primary: (digit) => this.controllerPrimary(digit),
      secondary: () => this.controllerSecondary(),
      notes: () => this.controllerAction(() => this.toggleNotes()),
      hint: () => this.controllerAction(() => this.openHint()),
      undo: () => this.controllerAction(() => this.undo()),
      redo: () => this.controllerAction(() => this.redo()),
      pause: () => this.controllerPause(),
      status: (status) => {
        this.controllerState = status;
        this.updateControllerUI();
      },
    });
    this.store.subscribe((state, action) => this.onStateChange(state, action));
  }

  async initialize() {
    try {
      const [
        catalog,
        manifest,
        dialogue,
        savedSettings,
        active,
        completions,
        unlockRecords,
        resetEpoch,
      ] = await Promise.all([
        loadJson("content/catalog"),
        loadJson("asset-manifest"),
        loadJson("content/companion-dialogue"),
        this.persistence.settings(),
        this.persistence.loadActive(),
        this.persistence.all("completed_games"),
        this.persistence.all("unlocks"),
        this.persistence.statisticsEpoch(),
      ]);
      await this.persistence.seedCatalog(catalog);
      const settings = { ...DEFAULT_SETTINGS, ...savedSettings };
      const filteredCompletions = resetEpoch
        ? completions.filter((record) => record.completed_at >= resetEpoch)
        : completions;
      const recovered = active ?? loadEmergencySession();
      const activeGame =
        recovered &&
        catalog.puzzles.some((puzzle) => puzzle.id === recovered.puzzle_id)
          ? restoreGame(recovered)
          : null;
      this.assets = new AssetService(manifest);
      this.director = new CompanionDirector(dialogue, {
        state: activeGame?.companion_state,
      });
      this.commands = new GameCommandService({
        store: this.store,
        catalog,
        director: this.director,
        onComplete: (record) => this.persistCompletion(record),
        onEvents: (events) => this.handleCommandEvents(events),
        onCommit: (_receipt, action) => {
          if (!["advance_time", "select_cell"].includes(action.type))
            this.scheduleSave();
          this.scheduleIdle();
        },
      });
      this.store.dispatch({
        type: "BOOTSTRAP",
        payload: {
          catalog,
          manifest,
          settings,
          activeGame,
          completions: filteredCompletions,
          unlocks: unlockRecords.map((record) => record.collection_item_id),
          route: parseRoute(),
        },
      });
      this.applyTheme(settings.theme);
      this.bindEvents();
      this.gamepad.start();
      this.speak(activeGame ? "session_resume" : this.director.sessionEvent(), {
        force: true,
        priority: 95,
      });
      this.updateManager = new UpdateManager({
        buildId: BUILD_ID,
        save: () => this.forceSave(),
        restoreRoute: () => location.hash || "#/home",
      });
      if (import.meta.env.PROD) await this.updateManager.start();
      this.startTimers();
    } catch (error) {
      console.error(error);
      this.root.innerHTML = `<main class="fatal"><h1>The lodge could not open</h1><p>${escapeHtml(error.message)}</p><button class="button primary" data-reload>Try again</button></main>`;
      this.root
        .querySelector("[data-reload]")
        ?.addEventListener("click", () => location.reload());
    }
  }

  bindEvents() {
    window.addEventListener("hashchange", () => this.routeChanged());
    window.addEventListener("pointerdown", () => {
      document.body.dataset.inputModality = "pointer";
    });
    window.addEventListener("keydown", (event) => {
      if (event.key === "Tab" || event.key.startsWith("Arrow"))
        document.body.dataset.inputModality = "keyboard";
      this.onPlayShortcut(event);
    });
    this.root.addEventListener("click", (event) => this.onClick(event));
    this.root.addEventListener("change", (event) => this.onChange(event));
    this.root.addEventListener("submit", (event) => this.onSubmit(event));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        if (
          this.state.settings.pauseOnBackground &&
          this.state.activeGame?.timer_running
        )
          this.commands.execute({ type: "pause_game" }, { exposure: "human" });
        this.forceSave();
      }
    });
    window.addEventListener("beforeunload", () => this.emergencySnapshot());
  }

  startTimers() {
    window.setInterval(() => {
      const now = performance.now();
      const elapsed = Math.min(2000, now - this.lastTick);
      this.lastTick = now;
      if (this.state.activeGame?.timer_running)
        this.commands.execute(
          { type: "advance_time", milliseconds: Math.round(elapsed) },
          { exposure: "human" },
        );
    }, 1000);
    this.scheduleIdle();
  }

  get state() {
    return this.store.getState();
  }
  get catalog() {
    return this.state.catalog;
  }
  puzzle(id = this.state.activeGame?.puzzle_id) {
    return this.catalog?.puzzles.find((puzzle) => puzzle.id === id);
  }
  item(id) {
    return this.catalog?.collection_items.find((item) => item.id === id);
  }
  pack(id) {
    return this.catalog?.packs.find((pack) => pack.id === id);
  }

  onStateChange(state, action) {
    if (!state.ready) return;
    if (
      ["UPDATE_GAME", "COMPANION_LINE"].includes(action.type) &&
      state.route.name === "play"
    )
      this.updatePlayView();
    else this.render();
  }

  routeChanged() {
    const route = parseRoute();
    this.store.dispatch({ type: "NAVIGATE", route });
    localStorage.setItem(
      "brawnyai-sudoku-last-route",
      location.hash || "#/home",
    );
    requestAnimationFrame(() =>
      this.root.querySelector("h1")?.focus({ preventScroll: true }),
    );
  }

  render() {
    if (!this.state.ready) return;
    this.applyBodySettings();
    const route = this.state.route;
    if (route.name === "play") {
      const puzzle = this.puzzle(route.params.puzzleId);
      if (!puzzle) return this.renderNotFound();
      if (
        !this.state.activeGame ||
        this.state.activeGame.puzzle_id !== puzzle.id
      )
        return this.startPuzzle(puzzle.id);
      this.renderPlay();
      return;
    }
    this.renderedPlayId = null;
    this.board = null;
    const content = this.screen(route);
    this.root.innerHTML = `<div class="app-shell"><header class="site-header"><a class="brand" href="#/home"><span>BrawnyAi</span> Sudoku</a><nav aria-label="Primary"><a href="#/daily">Daily</a><a href="#/journey">Journey</a><a href="#/collection">Stars</a><a href="#/learn">Learn</a><a href="#/settings" aria-label="Settings">⚙</a></nav></header><main id="main-content" class="content-screen">${content}</main>${this.renderDialog()}</div>`;
  }

  screen(route) {
    switch (route.name) {
      case "home":
        return this.homeScreen();
      case "daily":
        return this.dailyScreen();
      case "archive":
        return this.archiveScreen();
      case "journey":
        return this.journeyScreen();
      case "pack":
        return this.packScreen(route.params.packId);
      case "item":
        return this.itemScreen(route.params.collectionItemId);
      case "quick-play":
        return this.quickPlayScreen();
      case "complete":
        return this.completionScreen(route.params.puzzleId);
      case "collection":
        return this.collectionScreen();
      case "stats":
        return this.statsScreen();
      case "learn":
        return this.learnScreen();
      case "settings":
        return this.settingsScreen();
      case "about":
        return this.aboutScreen();
      default:
        return this.notFoundContent();
    }
  }

  homeScreen() {
    const daily = this.todayEntry();
    const puzzle = this.puzzle(daily?.entry.puzzle_id);
    const item = puzzle
      ? this.item(puzzle.collection_item_id)
      : this.catalog.collection_items[0];
    const stats = this.derivedStats();
    const portrait = this.assets.portrait("the_jock");
    const recentUnlocks = this.state.unlocks
      .slice(-4)
      .reverse()
      .map((id) => this.item(id))
      .filter(Boolean);
    return `<section class="hero"><div><p class="eyebrow">Discover · Solve · Reveal · Collect</p><h1 tabindex="-1">Every grid reveals a new star.</h1><p class="lead">Solve handcrafted Sudoku puzzles, uncover collectible BrawnyAi artwork, and build your Constellation of Stars.</p><div class="button-row">${this.state.activeGame && !this.state.activeGame.completed_at ? `<a class="button primary" href="#/play/${this.state.activeGame.puzzle_id}">Continue puzzle</a>` : `<a class="button primary" href="#/daily">Play today's puzzle</a>`}<a class="button secondary" href="#/quick-play">Quick Play</a></div><dl class="hero-stats"><div><dt>Daily streak</dt><dd>${stats.currentStreak}</dd></div><div><dt>Stars found</dt><dd>${this.state.unlocks.length}/12</dd></div><div><dt>Completed</dt><dd>${stats.completed}</dd></div></dl></div>${this.companionCard(portrait, "Your puzzle companion")}</section><section><div class="section-heading"><div><p class="eyebrow">Today's challenge</p><h2>${escapeHtml(item.title)}</h2></div><a href="#/archive">Daily archive</a></div>${this.featureCard(item, puzzle, daily?.date)}</section><section class="home-links"><a class="feature-link" href="#/journey"><strong>Lumberlandia Journey</strong><span>Explore three collectible packs</span></a><a class="feature-link" href="#/collection"><strong>Constellation of Stars</strong><span>View medals and unlocked artwork</span></a><a class="feature-link" href="#/stats"><strong>Statistics</strong><span>See your real saved results</span></a></section>${recentUnlocks.length ? `<section><h2>Recently unlocked</h2><div class="card-grid">${recentUnlocks.map((entry) => this.collectibleCard(entry, true)).join("")}</div></section>` : ""}<section class="intro-note"><strong>New to BrawnyAi Sudoku?</strong><p>Choose any puzzle. Correct progress gradually reveals its collectible, and completing any difficulty unlocks the full star.</p></section>`;
  }

  dailyScreen() {
    const daily = this.todayEntry();
    if (!daily)
      return `<h1 tabindex="-1">Daily Puzzle</h1><p>No published Daily Puzzle is available for this date.</p><a class="button primary" href="#/archive">Open archive</a>`;
    const puzzle = this.puzzle(daily.entry.puzzle_id);
    const item = this.item(puzzle.collection_item_id);
    const done = this.state.completions.some(
      (record) => record.daily_date === daily.date,
    );
    return `<p class="eyebrow">${formatDate(daily.date)}</p><h1 tabindex="-1">Daily Puzzle</h1>${this.featureCard(item, puzzle, daily.date)}<div class="button-row"><button class="button primary" data-start-puzzle="${puzzle.id}" data-daily-date="${daily.date}">${done ? "Replay today's puzzle" : this.state.activeGame?.puzzle_id === puzzle.id ? "Continue today's puzzle" : "Start today's puzzle"}</button><a class="button secondary" href="#/archive">Previous Daily Puzzles</a></div>`;
  }

  archiveScreen() {
    const today = this.todayString();
    const completedDates = new Set(
      this.state.completions
        .filter((record) => record.daily_date)
        .map((record) => record.daily_date),
    );
    return `<div class="section-heading"><div><p class="eyebrow">365 published challenges</p><h1 tabindex="-1">Daily Archive</h1></div><a href="#/daily">Today</a></div><div class="archive-list">${Object.entries(
      this.catalog.daily.entries,
    )
      .map(([date, entry]) => {
        const puzzle = this.puzzle(entry.puzzle_id);
        const item = this.item(puzzle.collection_item_id);
        const future = date > today;
        return `<article class="archive-row ${future ? "future" : ""}"><img src="${this.assets.thumbnail(item.id).url}" alt="" loading="lazy" decoding="async" ${this.focalStyle(item)}><div><strong>${formatDate(date)}</strong><span>${escapeHtml(item.title)} · ${capitalize(puzzle.difficulty.label)}</span></div><span class="status">${future ? "Upcoming" : completedDates.has(date) ? "Completed ✓" : "Available"}</span>${future ? "" : `<button class="button compact" data-start-puzzle="${puzzle.id}" data-daily-date="${date}">Play</button>`}</article>`;
      })
      .join("")}</div>`;
  }

  journeyScreen() {
    return `<p class="eyebrow">Collectible progression</p><h1 tabindex="-1">Lumberlandia Journey</h1><p class="lead">Complete any difficulty to reveal a star. Return for the other medals.</p><div class="pack-list">${this.catalog.packs
      .map((pack) => {
        const unlocked = pack.item_ids.filter((id) =>
          this.state.unlocks.includes(id),
        ).length;
        const coverUrl = new URL(
          this.assets.packCover(pack.id).url,
          document.baseURI,
        ).href;
        return `<a class="pack-card" href="#/pack/${pack.id}" style="--pack-image:url('${coverUrl}')"><span class="pack-copy"><strong>${escapeHtml(pack.title)}</strong><span>${escapeHtml(pack.description)}</span><span>${unlocked}/${pack.item_ids.length} stars · ${Math.round((unlocked / pack.item_ids.length) * 100)}%</span></span></a>`;
      })
      .join("")}</div>`;
  }

  packScreen(packId) {
    const pack = this.pack(packId);
    if (!pack) return this.notFoundContent();
    const items = pack.item_ids.map((id) => this.item(id));
    return `<p class="eyebrow">Journey pack</p><h1 tabindex="-1">${escapeHtml(pack.title)}</h1><p class="lead">${escapeHtml(pack.description)}</p><div class="card-grid">${items.map((item) => this.collectibleCard(item, this.state.unlocks.includes(item.id))).join("")}</div>`;
  }

  itemScreen(itemId) {
    const item = this.item(itemId);
    if (!item) return this.notFoundContent();
    const unlocked = this.state.unlocks.includes(item.id);
    const completions = this.state.completions.filter(
      (record) => record.collection_item_id === item.id,
    );
    return `<div class="item-detail"><div class="poster-frame ${unlocked ? "" : "locked"}"><img src="${this.assets.poster(item.id).url}" alt="${unlocked ? escapeHtml(item.alt) : "Locked collectible"}" ${this.focalStyle(item)}></div><div><p class="eyebrow">${escapeHtml(this.pack(item.pack_id).title)}</p><h1 tabindex="-1">${escapeHtml(item.title)}</h1><p>${unlocked ? escapeHtml(item.alt) : "Complete any difficulty to reveal this collectible."}</p><h2>Choose a difficulty</h2><div class="difficulty-list">${[
      "beginner",
      "easy",
      "medium",
      "hard",
    ]
      .map((difficulty) => {
        const puzzleId = item.puzzle_ids[difficulty][0];
        const result = completions.find(
          (record) => record.difficulty === difficulty,
        );
        return `<button class="difficulty-button" data-start-puzzle="${puzzleId}"><span>${capitalize(difficulty)}</span><span>${result ? `★ ${formatDuration(result.elapsed_active_ms)}` : "Unplayed"}</span></button>`;
      })
      .join("")}</div></div></div>`;
  }

  quickPlayScreen() {
    return `<p class="eyebrow">Pick your pace</p><h1 tabindex="-1">Quick Play</h1><form id="quick-form" class="settings-form"><label>Difficulty<select name="difficulty">${["beginner", "easy", "medium", "hard"].map((value) => `<option value="${value}">${capitalize(value)}</option>`).join("")}</select></label><label>Pack<select name="pack"><option value="any">Any pack</option>${this.catalog.packs.map((pack) => `<option value="${pack.id}">${escapeHtml(pack.title)}</option>`).join("")}</select></label><label>Checking<select name="checking"><option value="relaxed">Relaxed — conflicts only</option><option value="standard">Standard — check solution</option></select></label><label>Theme<select name="theme">${this.catalog.themes.map((theme) => `<option value="${theme.id}">${escapeHtml(theme.title)}</option>`).join("")}</select></label><button class="button primary" type="submit">Find a puzzle</button></form>`;
  }

  collectionScreen() {
    return `<div class="section-heading"><div><p class="eyebrow">Your local collection</p><h1 tabindex="-1">Constellation of Stars</h1></div><span>${this.state.unlocks.length}/12 unlocked</span></div><div class="card-grid">${this.catalog.collection_items.map((item) => this.collectibleCard(item, this.state.unlocks.includes(item.id))).join("")}</div>`;
  }

  statsScreen() {
    const stats = this.derivedStats();
    return `<p class="eyebrow">Saved results only</p><h1 tabindex="-1">Statistics</h1><div class="stats-grid">${[
      ["Games started", stats.started],
      ["Games completed", stats.completed],
      ["Completion rate", `${stats.completionRate}%`],
      ["Current Daily streak", stats.currentStreak],
      ["Longest Daily streak", stats.longestStreak],
      ["Mistake-free finishes", stats.mistakeFree],
      ["Hint-free finishes", stats.hintFree],
      ["Total hints", stats.hints],
      ["Direct reveals", stats.reveals],
      ["Collection progress", `${this.state.unlocks.length}/12`],
    ]
      .map(
        ([label, value]) =>
          `<div><span>${label}</span><strong>${value}</strong></div>`,
      )
      .join(
        "",
      )}</div><h2>Best active time</h2><div class="medal-row">${["beginner", "easy", "medium", "hard"].map((difficulty) => `<span><strong>${capitalize(difficulty)}</strong>${stats.bestTimes[difficulty] ? formatDuration(stats.bestTimes[difficulty]) : "—"}</span>`).join("")}</div><h2>Recent results</h2>${
      this.state.completions.length
        ? `<ol class="result-list">${[...this.state.completions]
            .sort((a, b) => b.completed_at.localeCompare(a.completed_at))
            .slice(0, 10)
            .map(
              (record) =>
                `<li><span>${escapeHtml(this.item(record.collection_item_id)?.title ?? record.collection_item_id)}</span><span>${capitalize(record.difficulty)} · ${formatDuration(record.elapsed_active_ms)}</span></li>`,
            )
            .join("")}</ol>`
        : "<p>No completed puzzles yet.</p>"
    }<button class="button danger" data-dialog="reset-stats">Reset statistics</button>`;
  }

  learnScreen() {
    const techniques = [
      [
        "Sudoku rules",
        "Fill every row, column, and 3×3 box with digits 1 through 9, without repeats.",
      ],
      [
        "Candidate notes",
        "Small candidate marks record digits that may still fit an empty cell.",
      ],
      [
        "Naked single",
        "A cell with only one remaining candidate must take that digit.",
      ],
      [
        "Hidden single",
        "A digit that can appear in only one cell of a region belongs there.",
      ],
      [
        "Locked candidates",
        "When a candidate is confined to one line inside a box, remove it from the rest of that line.",
      ],
      [
        "Naked pair",
        "Two cells sharing only the same two candidates reserve those digits.",
      ],
      [
        "Hidden pair",
        "Two digits limited to the same two cells allow other candidates to be removed.",
      ],
      [
        "Triples",
        "Three cells or digits can form the same kind of restriction as a pair.",
      ],
      [
        "X-Wing",
        "Two matching candidate pairs in two rows or columns can eliminate that digit along crossing lines.",
      ],
    ];
    return `<p class="eyebrow">Build your solving toolkit</p><h1 tabindex="-1">Learn Sudoku</h1><div class="learn-list">${techniques.map(([title, text], index) => `<article><div class="mini-grid" aria-label="Semantic example grid for ${title}">${Array.from({ length: 9 }, (_, cell) => `<span${cell === index % 9 ? ' class="marked"' : ""}>${cell === index % 9 ? (index % 9) + 1 : ""}</span>`).join("")}</div><div><h2>${title}</h2><p>${text}</p></div></article>`).join("")}</div>`;
  }

  settingsScreen() {
    const s = this.state.settings;
    const checkbox = (key, label) =>
      `<label class="toggle"><span>${label}</span><input type="checkbox" data-setting="${key}" ${s[key] ? "checked" : ""}></label>`;
    return `<p class="eyebrow">Make the lodge yours</p><h1 tabindex="-1">Settings</h1><div class="settings-sections"><section><h2>Gameplay</h2>${checkbox("showConflicts", "Show row, column, and box conflicts")}${checkbox("checkAgainstSolution", "Check entries against the solution")}${checkbox("autoRemovePeerNotes", "Automatically remove peer notes")}${checkbox("hideCompletedDigits", "Hide completed digits")}${checkbox("highlightMatchingDigits", "Highlight matching digits")}${checkbox("showTimer", "Show active timer")}</section><section><h2>Companion and sensory</h2><label>Jock frequency<select data-setting="companionFrequency">${["off", "low", "normal", "high"].map((value) => `<option value="${value}" ${s.companionFrequency === value ? "selected" : ""}>${capitalize(value)}</option>`).join("")}</select></label>${checkbox("sound", "Sound")}${checkbox("music", "Music")}${checkbox("haptics", "Haptics when available")}${checkbox("reducedMotion", "Reduced motion")}</section><section><h2>Accessibility</h2>${checkbox("highContrast", "High contrast")}${checkbox("largeInteractionMode", "Large interaction mode")}${checkbox("pauseOnBackground", "Pause when app is hidden")}<label>Screen-reader verbosity<select data-setting="screenReaderVerbosity">${["quiet", "normal", "detailed"].map((value) => `<option value="${value}" ${s.screenReaderVerbosity === value ? "selected" : ""}>${capitalize(value)}</option>`).join("")}</select></label></section><section><h2>Theme</h2><label>Visual theme<select data-setting="theme">${this.catalog.themes.map((theme) => `<option value="${theme.id}" ${s.theme === theme.id ? "selected" : ""}>${escapeHtml(theme.title)}</option>`).join("")}</select></label></section></div><p><a href="#/about">About, privacy, and licenses</a></p>`;
  }

  aboutScreen() {
    return `<p class="eyebrow">Local-first by design</p><h1 tabindex="-1">About BrawnyAi Sudoku</h1><p class="lead">A collectible Sudoku game built with semantic HTML, CSS, and JavaScript. The Jock reacts only to verified public events and never decides puzzle truth.</p><h2>Privacy</h2><p>No account, analytics, cloud storage, payment, or remote gameplay service is required. Progress stays in this browser or app installation.</p><h2>Updates</h2><p>Browser and installed PWA releases save the active puzzle, refresh automatically, and restore your place. Native Capacitor releases update through their platform store.</p><h2>Build</h2><p>Version <code>${escapeHtml(BUILD_ID)}</code></p><p>Temporary collectible posters are replaceable source assets. The Jock portrait is authorized BrawnyAi SFW media copied from the Chess project.</p>`;
  }

  completionScreen(puzzleId) {
    const puzzle = this.puzzle(puzzleId);
    if (!puzzle) return this.notFoundContent();
    const item = this.item(puzzle.collection_item_id);
    const result =
      [...this.state.completions]
        .reverse()
        .find((record) => record.puzzle_id === puzzleId) ??
      (this.state.activeGame?.puzzle_id === puzzleId
        ? this.completionRecord(this.state.activeGame, puzzle)
        : null);
    const video = this.assets.video(item.id);
    return `<div class="completion-layout"><div class="completion-media">${video ? `<video src="${video.url}" poster="${this.assets.poster(item.id).url}" controls playsinline autoplay></video>` : `<img src="${this.assets.poster(item.id).url}" alt="${escapeHtml(item.alt)}" ${this.focalStyle(item)}>`}</div><div><p class="eyebrow">New star revealed</p><h1 tabindex="-1">${escapeHtml(item.title)}</h1><p class="lead">${escapeHtml(this.state.companionLine?.text ?? "Strong finish. This collectible is yours.")}</p>${result ? `<dl class="result-details"><div><dt>Difficulty</dt><dd>${capitalize(result.difficulty)}</dd></div><div><dt>Active time</dt><dd>${formatDuration(result.elapsed_active_ms)}</dd></div><div><dt>Mistakes</dt><dd>${result.mistakes}</dd></div><div><dt>Hints</dt><dd>${result.hints_used}</dd></div><div><dt>Reveals</dt><dd>${result.reveals_used}</dd></div></dl>` : ""}<div class="button-row"><a class="button primary" href="#/collection">View Constellation</a><button class="button secondary" data-start-puzzle="${puzzle.id}">Replay puzzle</button><button class="button secondary" data-share="${puzzle.id}">Share result</button><a class="button secondary" href="#/home">Home</a></div></div></div>`;
  }

  renderPlay() {
    const game = this.state.activeGame;
    const puzzle = this.puzzle();
    const item = this.item(puzzle.collection_item_id);
    this.renderedPlayId = puzzle.id;
    this.root.innerHTML = `<main id="main-content" class="play-screen"><header class="play-header"><a class="icon-button" href="#/home" aria-label="Back to Home">←</a><div><span>${capitalize(puzzle.difficulty.label)}</span><strong data-timer>${formatDuration(game.elapsed_active_ms)}</strong></div><button class="icon-button" data-action="pause" aria-label="Pause puzzle">Ⅱ</button></header><section class="play-layout"><aside class="reveal-panel"><figure class="reveal-media" style="--reveal:${game.reveal_progress}" aria-label="Collectible reveal progress for ${escapeHtml(item.title)}"><img src="${this.assets.poster(item.id).url}" alt="" ${this.focalStyle(item)}><span class="reveal-cover"></span><figcaption class="reveal-caption"><strong>${escapeHtml(item.title)}</strong><span>${Math.round(game.reveal_progress * 100)}% revealed</span></figcaption></figure>${this.companionCard(this.assets.portrait("the_jock"), "Puzzle companion", true)}</aside><section class="board-column"><h1 class="sr-only" tabindex="-1">${escapeHtml(item.title)} Sudoku</h1><div id="sudoku-board"></div><div class="board-actions"><button data-action="undo">↶ <span>Undo</span></button><button data-action="redo">↷ <span>Redo</span></button><button data-action="erase">⌫ <span>Erase</span></button><button data-action="notes" aria-pressed="${game.note_mode}">✎ <span>Notes</span></button><button data-action="hint" aria-label="Ask The Jock for a hint">✦ <span>Hint</span></button></div><div class="number-pad" aria-label="Number pad">${Array.from({ length: 9 }, (_, index) => `<button data-digit="${index + 1}">${index + 1}</button>`).join("")}</div>${this.inputGuide()}${this.state.settings.largeInteractionMode ? `<div class="magnifier" aria-live="polite">Selected: row ${Math.floor(game.selected_cell / 9) + 1}, column ${(game.selected_cell % 9) + 1}</div>` : ""}</section></section>${this.renderDialog()}</main>`;
    this.board = new SudokuBoard(this.root.querySelector("#sudoku-board"), {
      select: (cell) => this.select(cell),
      digit: (digit) => this.enterDigit(digit),
      erase: () => this.erase(),
      notes: () => this.toggleNotes(),
      undo: () => this.undo(),
      redo: () => this.redo(),
      hint: () => this.openHint(),
      pause: () => this.pause(),
    });
    this.updatePlayView();
  }

  updatePlayView() {
    if (!this.board || !this.state.activeGame) return;
    const game = this.state.activeGame;
    const puzzle = this.puzzle();
    this.board.render(
      game,
      this.state.settings,
      visibleBoard(game, puzzle, this.state.settings),
    );
    const timer = this.root.querySelector("[data-timer]");
    if (timer) {
      timer.textContent = formatDuration(game.elapsed_active_ms);
      timer.hidden = !this.state.settings.showTimer;
    }
    const notes = this.root.querySelector('[data-action="notes"]');
    notes?.setAttribute("aria-pressed", String(game.note_mode));
    notes?.classList.toggle("active", game.note_mode);
    this.root
      .querySelector('[data-action="undo"]')
      ?.toggleAttribute("disabled", !game.history.length);
    this.root
      .querySelector('[data-action="redo"]')
      ?.toggleAttribute("disabled", !game.redo.length);
    const cover = this.root.querySelector(".reveal-cover");
    if (cover)
      cover.style.opacity = String(
        Math.max(0, 0.88 - game.reveal_progress * 0.88),
      );
    const revealProgress = this.root.querySelector(".reveal-caption span");
    if (revealProgress)
      revealProgress.textContent = `${Math.round(game.reveal_progress * 100)}% revealed`;
    this.updateCompanionCard();
    const hintButton = this.root.querySelector('[data-action="hint"]');
    const hintButtonLabel = hintButton?.querySelector("span");
    if (hintButtonLabel) {
      const atFinalStage =
        game.hint && game.hint_stage >= game.hint.stages.length - 1;
      hintButtonLabel.textContent = game.hint
        ? atFinalStage
          ? "Apply"
          : "Next clue"
        : "Hint";
      hintButton.setAttribute(
        "aria-label",
        game.hint
          ? atFinalStage
            ? "Apply hint"
            : "Show next hint clue"
          : "Ask The Jock for a hint",
      );
    }
    this.updateControllerUI();
    this.renderDialogIntoPlay();
  }

  inputGuide() {
    return `<details class="input-guide"><summary><span data-controller-summary aria-live="polite">Keyboard ready · Controller not connected</span><strong>Controls</strong></summary><div class="input-guide-panel"><section><h2>Keyboard</h2><p><kbd>Arrows</kbd> or <kbd>WASD</kbd> move · <kbd>1–9</kbd> enter · <kbd>0</kbd>/<kbd>Delete</kbd> erase</p><p><kbd>N</kbd> notes · <kbd>H</kbd> hint · <kbd>U</kbd> undo · <kbd>R</kbd> redo · <kbd>Esc</kbd> pause</p></section><section><h2>Controller</h2><p>D-pad/stick moves · LB/RB chooses a number · A/Cross enters</p><p>X/Square notes · B/Circle erase · Y/Triangle hint · triggers undo/redo · Menu pauses</p></section></div></details>`;
  }

  updateControllerUI() {
    const summary = this.root.querySelector("[data-controller-summary]");
    if (!summary) return;
    const { connected, name, digit } = this.controllerState;
    summary.textContent = connected
      ? `${name || "Controller"} ready · selected number ${digit}`
      : "Keyboard ready · Controller not connected";
    this.root
      .querySelector(".input-guide")
      ?.classList.toggle("controller-connected", connected);
  }

  companionCard(portrait, label, compact = false) {
    const presentation = this.companionPresentation(compact);
    return `<aside class="companion-card ${compact ? "compact" : ""} ${presentation.hintActive ? "hint-active" : ""}" aria-label="${label}">${portrait?.url ? `<img src="${portrait.url}" alt="The Jock, your BrawnyAi Sudoku companion" decoding="async">` : `<span class="companion-fallback" aria-hidden="true">J</span>`}<div class="companion-copy"><span class="eyebrow companion-heading">${escapeHtml(presentation.heading)}</span><p class="companion-line">${escapeHtml(presentation.text)}</p><div class="hint-inline-actions">${presentation.actions}</div></div></aside>`;
  }

  companionPresentation(compact = false) {
    const game = this.state.activeGame;
    if (compact && game?.hint) {
      const hint = game.hint;
      const stageIndex = Math.min(game.hint_stage, hint.stages.length - 1);
      const stage = hint.stages[stageIndex];
      const finalStage = stageIndex === hint.stages.length - 1;
      return {
        hintActive: true,
        heading: `The Jock · ${capitalize(hint.technique.replaceAll("_", " "))} · ${stageIndex + 1}/${hint.stages.length}`,
        text: stage.text,
        actions: `${finalStage ? `<button class="hint-inline-primary" data-action="hint-apply">Apply step</button>` : `<button class="hint-inline-primary" data-action="hint-next">Next clue</button>`}<button data-action="dismiss-hint">Dismiss</button><button data-action="reveal-cell">Reveal cell</button>`,
      };
    }
    return {
      hintActive: false,
      heading: "The Jock",
      text: this.state.companionLine?.text ?? "One clean deduction at a time.",
      actions: "",
    };
  }

  updateCompanionCard() {
    const card = this.root.querySelector(".companion-card.compact");
    if (!card) return;
    const presentation = this.companionPresentation(true);
    card.classList.toggle("hint-active", presentation.hintActive);
    const heading = card.querySelector(".companion-heading");
    const commentary = card.querySelector(".companion-line");
    const actions = card.querySelector(".hint-inline-actions");
    if (heading) heading.textContent = presentation.heading;
    if (commentary) commentary.textContent = presentation.text;
    if (actions) actions.innerHTML = presentation.actions;
  }

  featureCard(item, puzzle, date) {
    return `<article class="daily-feature"><img src="${this.assets.poster(item.id).url}" alt="${escapeHtml(item.alt)}" decoding="async" ${this.focalStyle(item)}><div><span>${date ? formatDate(date) : "Featured"}</span><strong>${escapeHtml(item.title)}</strong><span>${capitalize(puzzle.difficulty.label)} · ${puzzle.clue_count} givens</span></div></article>`;
  }

  collectibleCard(item, unlocked) {
    const results = this.state.completions.filter(
      (record) => record.collection_item_id === item.id,
    );
    return `<a class="collectible-card ${unlocked ? "unlocked" : "locked"}" href="#/item/${item.id}"><img src="${this.assets.thumbnail(item.id).url}" alt="${unlocked ? escapeHtml(item.alt) : "Locked collectible"}" loading="lazy" decoding="async" ${this.focalStyle(item)}><div><strong>${escapeHtml(item.title)}</strong><span>${unlocked ? "Unlocked" : "Solve to reveal"}</span><span class="medals">${["beginner", "easy", "medium", "hard"].map((difficulty) => `<i class="${results.some((record) => record.difficulty === difficulty) ? "earned" : ""}" title="${capitalize(difficulty)} medal">★</i>`).join("")}</span></div></a>`;
  }

  focalStyle(item) {
    const x = Math.round((item.focal_point?.x ?? 0.5) * 10000) / 100;
    const y = Math.round((item.focal_point?.y ?? 0.5) * 10000) / 100;
    return `style="object-position:${x}% ${y}%"`;
  }

  renderDialog() {
    const dialog = this.state.dialog;
    if (!dialog) return "";
    if (dialog === "pause")
      return `<div class="dialog-backdrop"><section class="dialog" role="dialog" aria-modal="true" aria-labelledby="dialog-title"><h2 id="dialog-title">Puzzle paused</h2><p>Your active timer is stopped and progress is saved.</p><button class="button primary" data-action="resume">Resume</button><a class="button secondary" href="#/home">Save and go Home</a></section></div>`;
    if (dialog === "reset-stats")
      return `<div class="dialog-backdrop"><section class="dialog" role="dialog" aria-modal="true" aria-labelledby="reset-title"><h2 id="reset-title">Reset statistics?</h2><p>This hides completed results from statistics. Settings and unlocked collectibles remain.</p><button class="button danger" data-action="confirm-reset-stats">Reset statistics</button><button class="button secondary" data-action="close-dialog">Cancel</button></section></div>`;
    return "";
  }

  renderDialogIntoPlay() {
    const existing = this.root.querySelector(".dialog-backdrop");
    const html = this.renderDialog();
    if (existing && !html) existing.remove();
    else if (!existing && html)
      this.root
        .querySelector(".play-screen")
        ?.insertAdjacentHTML("beforeend", html);
    else if (existing && html) existing.outerHTML = html;
  }

  async onClick(event) {
    const target = event.target.closest("button, a");
    if (!target) return;
    if (target.dataset.startPuzzle) {
      event.preventDefault();
      await this.startPuzzle(
        target.dataset.startPuzzle,
        target.dataset.dailyDate ?? null,
      );
      return;
    }
    if (target.dataset.digit)
      return this.enterDigit(Number(target.dataset.digit));
    if (target.dataset.dialog)
      return this.store.dispatch({
        type: "DIALOG",
        dialog: target.dataset.dialog,
      });
    if (target.dataset.share) return this.share(target.dataset.share);
    const action = target.dataset.action;
    if (!action) return;
    event.preventDefault();
    if (action === "undo") this.undo();
    else if (action === "redo") this.redo();
    else if (action === "erase") this.erase();
    else if (action === "notes") this.toggleNotes();
    else if (action === "hint") this.openHint();
    else if (action === "pause") this.pause();
    else if (action === "resume") this.resume();
    else if (action === "close-dialog")
      this.store.dispatch({ type: "CLOSE_DIALOG" });
    else if (action === "hint-next") this.advanceHint();
    else if (action === "hint-apply") this.applyCurrentHint();
    else if (action === "dismiss-hint") this.dismissHint();
    else if (action === "reveal-cell") this.revealSelected();
    else if (action === "confirm-reset-stats") this.resetStats();
  }

  onChange(event) {
    const input = event.target.closest("[data-setting]");
    if (!input) return;
    const value = input.type === "checkbox" ? input.checked : input.value;
    const settings = { [input.dataset.setting]: value };
    if (input.dataset.setting === "checkAgainstSolution")
      this.commands.execute(
        { type: "set_gameplay_options", check_against_solution: value },
        { exposure: "human" },
      );
    else if (input.dataset.setting === "autoRemovePeerNotes")
      this.commands.execute(
        { type: "set_gameplay_options", auto_remove_peer_notes: value },
        { exposure: "human" },
      );
    else this.store.dispatch({ type: "SETTINGS", settings });
    this.persistence.saveSettings(this.state.settings);
    if (input.dataset.setting === "theme") this.applyTheme(value);
  }

  async onSubmit(event) {
    const form = event.target.closest("#quick-form");
    if (!form) return;
    event.preventDefault();
    const data = new FormData(form);
    const difficulty = String(data.get("difficulty") ?? "beginner");
    const packId = String(data.get("pack") ?? "any");
    const checking = String(data.get("checking") ?? "relaxed");
    const theme = String(data.get("theme") ?? this.state.settings.theme);
    const completedIds = new Set(
      this.state.completions.map((record) => record.puzzle_id),
    );
    const eligible = this.catalog.puzzles.filter(
      (puzzle) =>
        puzzle.difficulty.label === difficulty &&
        (packId === "any" ||
          this.item(puzzle.collection_item_id)?.pack_id === packId),
    );
    const puzzle =
      eligible.find((candidate) => !completedIds.has(candidate.id)) ??
      eligible[0];
    if (!puzzle)
      return this.announce("No puzzle matches those Quick Play choices.");
    const settings = { checkAgainstSolution: checking === "standard", theme };
    this.store.dispatch({ type: "SETTINGS", settings });
    await this.persistence.saveSettings(this.state.settings);
    this.applyTheme(theme);
    await this.startPuzzle(puzzle.id);
  }

  async startPuzzle(puzzleId, dailyDate = null) {
    const puzzle = this.puzzle(puzzleId);
    if (!puzzle) return navigate("/not-found");
    const result = this.commands.execute(
      {
        type: "start_puzzle",
        puzzle_id: puzzleId,
        ...(dailyDate ? { daily_date: dailyDate } : {}),
      },
      { exposure: "human" },
    );
    if (!result.ok) return this.announce(result.error.message);
    await this.forceSave();
    navigate(`/play/${puzzle.id}`);
  }

  onPlayShortcut(event) {
    if (this.state.route.name !== "play" || !this.state.activeGame) return;
    const target =
      event.target instanceof globalThis.Element ? event.target : null;
    if (target?.closest('[role="gridcell"]')) return;
    const interactive = target?.closest(
      "button, a, input, select, textarea, summary, [contenteditable=true]",
    );
    const key = event.key.toLowerCase();
    if (this.state.dialog === "pause") {
      if (event.key === "Escape" || key === "p") {
        event.preventDefault();
        this.resume();
      }
      return;
    }
    if (interactive || this.state.activeGame.completed_at) return;
    let handled = true;
    if ((event.ctrlKey || event.metaKey) && key === "z" && !event.shiftKey)
      this.undo();
    else if (
      (event.ctrlKey || event.metaKey) &&
      (key === "y" || (key === "z" && event.shiftKey))
    )
      this.redo();
    else if (
      /^[1-9]$/.test(event.key) &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey
    )
      this.enterDigit(Number(event.key));
    else if (
      event.key === "0" ||
      event.key === "Backspace" ||
      event.key === "Delete"
    )
      this.erase();
    else if (event.key === "ArrowUp" || key === "w") this.moveSelected(-1, 0);
    else if (event.key === "ArrowDown" || key === "s") this.moveSelected(1, 0);
    else if (event.key === "ArrowLeft" || key === "a") this.moveSelected(0, -1);
    else if (event.key === "ArrowRight" || key === "d") this.moveSelected(0, 1);
    else if (key === "n") this.toggleNotes();
    else if (key === "h") this.openHint();
    else if (key === "u" && !event.ctrlKey && !event.metaKey) this.undo();
    else if (key === "r" && !event.ctrlKey && !event.metaKey) this.redo();
    else if (key === "p" || event.key === "Escape") this.pause();
    else handled = false;
    if (handled) {
      event.preventDefault();
      document.body.dataset.inputModality = "keyboard";
    }
  }

  moveSelected(rowChange, columnChange, focus = true) {
    const game = this.state.activeGame;
    if (!game || game.paused || game.completed_at) return;
    const row = Math.floor(game.selected_cell / 9);
    const column = game.selected_cell % 9;
    const destination =
      Math.max(0, Math.min(8, row + rowChange)) * 9 +
      Math.max(0, Math.min(8, column + columnChange));
    this.select(destination);
    if (focus) this.board?.focus(destination);
  }

  controllerMove(rowChange, columnChange) {
    if (
      this.state.route.name === "play" &&
      this.state.activeGame &&
      !this.state.dialog
    )
      this.moveSelected(rowChange, columnChange, false);
    else this.moveControllerFocus(rowChange, columnChange);
  }

  controllerPrimary(digit) {
    if (this.state.route.name === "play" && this.state.activeGame) {
      if (this.state.dialog === "pause") return this.resume();
      if (!this.state.dialog) this.enterDigit(digit);
      return;
    }
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      active.matches("a[href], button, input, select, summary, [tabindex='0']")
    )
      active.click();
    else this.moveControllerFocus(1, 0);
  }

  controllerSecondary() {
    if (this.state.route.name === "play" && this.state.activeGame) {
      if (this.state.dialog === "pause") this.resume();
      else if (!this.state.dialog) this.erase();
      return;
    }
    globalThis.history.back();
  }

  controllerAction(action) {
    if (
      this.state.route.name === "play" &&
      this.state.activeGame &&
      !this.state.dialog
    )
      action();
  }

  controllerPause() {
    if (this.state.route.name !== "play" || !this.state.activeGame) return;
    if (this.state.dialog === "pause") this.resume();
    else if (!this.state.dialog) this.pause();
  }

  moveControllerFocus(rowChange, columnChange) {
    const focusable = [
      ...this.root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex="0"]',
      ),
    ].filter((element) => element.getClientRects().length);
    if (!focusable.length) return;
    const current = document.activeElement;
    if (!(current instanceof HTMLElement) || !focusable.includes(current)) {
      focusable[0].focus();
      return;
    }
    const origin = current.getBoundingClientRect();
    const originX = origin.left + origin.width / 2;
    const originY = origin.top + origin.height / 2;
    const candidates = focusable
      .filter((element) => element !== current)
      .map((element) => {
        const rect = element.getBoundingClientRect();
        const dx = rect.left + rect.width / 2 - originX;
        const dy = rect.top + rect.height / 2 - originY;
        const forward = columnChange ? dx * columnChange : dy * rowChange;
        const sideways = columnChange ? Math.abs(dy) : Math.abs(dx);
        return { element, forward, score: forward + sideways * 1.8 };
      })
      .filter((candidate) => candidate.forward > 2)
      .sort((left, right) => left.score - right.score);
    (candidates[0]?.element ?? focusable[0]).focus();
  }

  select(cell) {
    this.commands.execute(
      { type: "select_cell", cell: indexToCellId(cell) },
      { exposure: "human" },
    );
  }
  enterDigit(digit) {
    const before = this.state.activeGame;
    const result = this.commands.execute(
      {
        type: before.note_mode ? "toggle_candidate_note" : "place_digit",
        cell: indexToCellId(before.selected_cell),
        digit,
      },
      { exposure: "human" },
    );
    if (!result.ok) return;
    this.platform.haptic(this.state.settings.haptics);
  }
  erase() {
    this.commands.execute(
      {
        type: "erase_cell",
        cell: indexToCellId(this.state.activeGame.selected_cell),
      },
      { exposure: "human" },
    );
  }
  toggleNotes() {
    const enabled = !this.state.activeGame.note_mode;
    const result = this.commands.execute(
      { type: "set_note_mode", enabled },
      { exposure: "human" },
    );
    if (!result.ok) return;
  }
  undo() {
    const result = this.commands.execute(
      { type: "undo" },
      { exposure: "human" },
    );
    if (!result.ok) return;
  }
  redo() {
    const result = this.commands.execute(
      { type: "redo" },
      { exposure: "human" },
    );
    if (!result.ok) return;
  }
  pause() {
    this.commands.execute({ type: "pause_game" }, { exposure: "human" });
    this.store.dispatch({ type: "DIALOG", dialog: "pause" });
  }
  resume() {
    this.commands.execute({ type: "resume_game" }, { exposure: "human" });
    this.store.dispatch({ type: "CLOSE_DIALOG" });
  }

  openHint() {
    if (this.state.activeGame.hint) {
      if (
        this.state.activeGame.hint_stage >=
        this.state.activeGame.hint.stages.length - 1
      )
        return this.applyCurrentHint();
      return this.advanceHint();
    }
    const result = this.commands.execute(
      { type: "request_hint" },
      { exposure: "human" },
    );
    if (!result.ok)
      return this.announce(
        `${result.error.message} You may use Reveal selected cell.`,
      );
    const hint = this.state.activeGame.hint;
    this.announce(`The Jock's first hint: ${hint.stages[0].text}`);
  }
  advanceHint() {
    const result = this.commands.execute(
      { type: "advance_hint" },
      { exposure: "human" },
    );
    if (!result.ok) return;
    const game = this.state.activeGame;
    const nextStage = game.hint_stage;
    this.announce(
      `The Jock's hint, clue ${nextStage + 1}: ${game.hint.stages[nextStage].text}`,
    );
  }
  applyCurrentHint() {
    const hint = this.state.activeGame.hint;
    if (!hint) return;
    const result = this.commands.execute(
      { type: "apply_hint" },
      { exposure: "human" },
    );
    if (!result.ok) return;
    this.announce("The verified hint step was applied.");
  }
  dismissHint() {
    this.commands.execute({ type: "dismiss_hint" }, { exposure: "human" });
    this.announce("Hint closed. Your board was not changed.");
  }
  revealSelected() {
    const result = this.commands.execute(
      {
        type: "reveal_cell",
        cell: indexToCellId(this.state.activeGame.selected_cell),
      },
      { exposure: "human" },
    );
    if (!result.ok) return;
  }

  async persistCompletion(record) {
    await this.persistence.archiveCompletion(record);
    await this.persistence.clearActive();
    navigate(`/complete/${record.puzzle_id}`);
  }

  completionRecord(game, puzzle) {
    return completionRecord(game, puzzle);
  }

  handleCommandEvents(events) {
    for (const item of events) {
      if (item.type === "companion_line")
        this.presentCompanionLine(item.data.line);
    }
  }
  scheduleSave() {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.forceSave(), 250);
  }
  async forceSave() {
    if (!this.state.activeGame || this.state.activeGame.completed_at) return;
    const record = {
      ...this.state.activeGame,
      companion_state: this.director?.snapshot() ?? null,
    };
    try {
      await this.persistence.saveActive(record);
    } catch (error) {
      console.error("IndexedDB save failed", error);
      localStorage.setItem(
        "brawnyai-sudoku-emergency-session",
        JSON.stringify(record).slice(0, 100_000),
      );
    }
  }
  emergencySnapshot() {
    if (!this.state.activeGame || this.state.activeGame.completed_at) return;
    try {
      localStorage.setItem(
        "brawnyai-sudoku-emergency-session",
        JSON.stringify({
          ...this.state.activeGame,
          companion_state: this.director?.snapshot(),
        }).slice(0, 100_000),
      );
    } catch {}
  }

  scheduleIdle() {
    clearTimeout(this.idleTimer);
    if (
      !this.state.activeGame ||
      this.state.activeGame.paused ||
      this.state.activeGame.completed_at
    )
      return;
    const actionCount = this.state.activeGame.action_count;
    this.idleTimer = setTimeout(() => {
      if (
        this.state.activeGame?.action_count === actionCount &&
        !this.state.activeGame.paused
      )
        this.speak("idle");
    }, 45_000);
  }

  speak(event, options = {}) {
    if (!this.director || this.state.settings.companionFrequency === "off")
      return;
    const line = this.director.line(
      event,
      {
        action_count: this.state.activeGame?.action_count ?? 0,
        frequency: this.state.settings.companionFrequency,
      },
      options,
    );
    if (!line) return;
    this.store.dispatch({ type: "COMPANION_LINE", line });
    this.presentCompanionLine(line);
  }
  presentCompanionLine(line) {
    if (this.state.settings.screenReaderVerbosity !== "quiet")
      this.announce(`The Jock says: ${line.text}`);
    const voice = this.assets.voice(line.voice_id);
    if (voice && this.state.settings.sound)
      new Audio(voice.url).play().catch(() => {});
  }
  announce(message) {
    const region = document.querySelector("#announcer");
    if (region) {
      region.textContent = "";
      requestAnimationFrame(() => {
        region.textContent = message;
      });
    }
  }

  async share(puzzleId) {
    const record = [...this.state.completions]
      .reverse()
      .find((item) => item.puzzle_id === puzzleId);
    const puzzle = this.puzzle(puzzleId);
    const pattern = puzzle.givens
      .split("")
      .map((value, index) =>
        value === "0" ? (index % 3 === 0 ? "□" : "·") : "■",
      )
      .join("")
      .match(/.{9}/g)
      .join("\n");
    const text = `BrawnyAi Sudoku\n${capitalize(puzzle.difficulty.label)}${record ? ` · ${formatDuration(record.elapsed_active_ms)} · ${record.mistakes} mistakes` : ""}\n${pattern}\nhttps://games.brawny.ai/sudoku/`;
    const result = await this.platform.share({
      title: "BrawnyAi Sudoku",
      text,
      url: "https://games.brawny.ai/sudoku/",
    });
    this.announce(
      result === "copied"
        ? "Result copied to clipboard."
        : result === "shared"
          ? "Share sheet opened."
          : "Sharing is unavailable.",
    );
  }

  async resetStats() {
    await this.persistence.resetStatistics();
    this.store.dispatch({ type: "RESET_STATS" });
    this.store.dispatch({ type: "CLOSE_DIALOG" });
  }

  derivedStats() {
    const completed = this.state.completions;
    const started =
      completed.length +
      (this.state.activeGame && !this.state.activeGame.completed_at ? 1 : 0);
    const dailyDates = [
      ...new Set(
        completed
          .filter((record) => record.daily_date)
          .map((record) => record.daily_date),
      ),
    ].sort();
    let longest = 0;
    let run = 0;
    let previous = null;
    for (const date of dailyDates) {
      const current = Date.parse(`${date}T12:00:00Z`);
      run =
        previous !== null && current - previous === 86_400_000 ? run + 1 : 1;
      longest = Math.max(longest, run);
      previous = current;
    }
    let currentStreak = 0;
    let cursor = this.todayString();
    while (dailyDates.includes(cursor)) {
      currentStreak += 1;
      cursor = new Date(Date.parse(`${cursor}T12:00:00Z`) - 86_400_000)
        .toISOString()
        .slice(0, 10);
    }
    const bestTimes = {};
    for (const difficulty of ["beginner", "easy", "medium", "hard"]) {
      const times = completed
        .filter((record) => record.difficulty === difficulty)
        .map((record) => record.elapsed_active_ms);
      if (times.length) bestTimes[difficulty] = Math.min(...times);
    }
    return {
      started,
      completed: completed.length,
      completionRate: started
        ? Math.round((completed.length / started) * 100)
        : 0,
      currentStreak,
      longestStreak: longest,
      mistakeFree: completed.filter((record) => !record.mistakes).length,
      hintFree: completed.filter(
        (record) => !record.hints_used && !record.reveals_used,
      ).length,
      hints: completed.reduce((sum, record) => sum + record.hints_used, 0),
      reveals: completed.reduce((sum, record) => sum + record.reveals_used, 0),
      bestTimes,
    };
  }

  todayString() {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: this.catalog.daily.timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const values = Object.fromEntries(
      parts.map((part) => [part.type, part.value]),
    );
    return `${values.year}-${values.month}-${values.day}`;
  }
  todayEntry() {
    const date = this.todayString();
    const entry = this.catalog.daily.entries[date];
    return entry ? { date, entry } : null;
  }

  applyTheme(themeId) {
    const theme =
      this.catalog?.themes.find((item) => item.id === themeId) ??
      this.catalog?.themes[0];
    if (!theme) return;
    for (const [key, value] of Object.entries(theme.tokens))
      document.documentElement.style.setProperty(
        `--${key.replaceAll("_", "-")}`,
        value,
      );
    localStorage.setItem("brawnyai-sudoku-theme", theme.id);
  }
  applyBodySettings() {
    document.body.classList.toggle(
      "reduced-motion",
      this.state.settings.reducedMotion,
    );
    document.body.classList.toggle(
      "high-contrast",
      this.state.settings.highContrast,
    );
    document.body.classList.toggle(
      "large-interaction",
      this.state.settings.largeInteractionMode,
    );
  }
  renderNotFound() {
    this.root.innerHTML = `<main class="fatal">${this.notFoundContent()}</main>`;
  }
  notFoundContent() {
    return `<h1 tabindex="-1">Trail not found</h1><p>That Sudoku route or content ID does not exist.</p><a class="button primary" href="#/home">Return Home</a>`;
  }
}

async function loadJson(name) {
  const response = await fetch(`./generated/${name}.json`);
  if (!response.ok) throw new Error(`Could not load ${name}.`);
  return response.json();
}
function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>"]/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[character],
  );
}
function capitalize(value) {
  return String(value ?? "")
    .replaceAll("_", " ")
    .replace(/^./, (character) => character.toUpperCase());
}
function formatDuration(milliseconds) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}
function formatDate(date) {
  return new Intl.DateTimeFormat("en", {
    dateStyle: "long",
    timeZone: "UTC",
  }).format(new Date(`${date}T12:00:00Z`));
}
function loadEmergencySession() {
  try {
    const value = JSON.parse(
      localStorage.getItem("brawnyai-sudoku-emergency-session") ?? "null",
    );
    return value?.state_version === 1 &&
      typeof value.puzzle_id === "string" &&
      value.values?.length === 81
      ? value
      : null;
  } catch {
    return null;
  }
}

const root = document.querySelector("#app");
if (root) new SudokuApp(root).initialize();
